import React, { useEffect, useRef, useState } from "react";

export type BigBlueMapLayerVisibility = {
  savedSpots: boolean;
  marketplace: boolean;
  tournaments: boolean;
  fishiq: boolean;
};

const BIG_BLUE_MAP_LAYER_VISIBILITY_KEY = "roddo.bigBlue.layers.v1";

export const DEFAULT_BIG_BLUE_MAP_LAYER_VISIBILITY: BigBlueMapLayerVisibility = {
  savedSpots: true,
  marketplace: true,
  tournaments: true,
  fishiq: true,
};

export const loadBigBlueMapLayerVisibility = (): BigBlueMapLayerVisibility => {
  try {
    const raw = window.localStorage.getItem(BIG_BLUE_MAP_LAYER_VISIBILITY_KEY);
    if (!raw) return { ...DEFAULT_BIG_BLUE_MAP_LAYER_VISIBILITY };
    const parsed = JSON.parse(raw);
    return {
      savedSpots: parsed?.savedSpots !== false,
      marketplace: parsed?.marketplace !== false,
      tournaments: parsed?.tournaments !== false,
      fishiq: parsed?.fishiq !== false,
    };
  } catch {
    return { ...DEFAULT_BIG_BLUE_MAP_LAYER_VISIBILITY };
  }
};

export const saveBigBlueMapLayerVisibility = (
  visibility: BigBlueMapLayerVisibility,
) => {
  try {
    window.localStorage.setItem(
      BIG_BLUE_MAP_LAYER_VISIBILITY_KEY,
      JSON.stringify(visibility),
    );
  } catch {}
};

function MapLayersIcon({
  className = "",
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={className}
      style={style}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.1"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m12 3 8 4.5-8 4.5-8-4.5L12 3Z" />
      <path d="m4 12 8 4.5 8-4.5" />
      <path d="m4 16.5 8 4.5 8-4.5" />
    </svg>
  );
}

type Props = {
  isDarkMode: boolean;
  visibility: BigBlueMapLayerVisibility;
  buttonClassName: string;
  onToggle: (key: keyof BigBlueMapLayerVisibility) => void;
  onBeforeOpen?: () => void;
};

const LAYERS: Array<[keyof BigBlueMapLayerVisibility, string, string]> = [
  ["savedSpots", "Saved Spots", "★"],
  ["marketplace", "Marketplace", "▤"],
  ["tournaments", "Tournaments", "🏆"],
  ["fishiq", "Water Reads", "🐟"],
];

export default function MapLayerControl({
  isDarkMode,
  visibility,
  buttonClassName,
  onToggle,
  onBeforeOpen,
}: Props) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (buttonRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [open]);

  const toggleOpen = () => {
    setOpen((current) => {
      const next = !current;
      if (next) onBeforeOpen?.();
      return next;
    });
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleOpen}
        className={[
          buttonClassName,
          open ? "ring-2 ring-[#2F80ED]/20 dark:ring-[#8A70F2]/25" : "",
        ].join(" ")}
        aria-label="Map layers"
        aria-expanded={open}
        title="Map layers"
      >
        <MapLayersIcon
          className="h-[22px] w-[22px]"
          style={{
            color: open
              ? isDarkMode
                ? "#C9BEFF"
                : "#6551B9"
              : isDarkMode
                ? "#BDEFF5"
                : "#2F80ED",
          }}
        />
      </button>

      {open ? (
        <div
          ref={panelRef}
          className="absolute right-[54px] top-0 z-[90] w-[220px] overflow-hidden rounded-[18px] border p-2 shadow-[0_16px_42px_rgba(25,30,42,.20)] backdrop-blur-xl dark:shadow-[0_18px_48px_rgba(0,0,0,.42)]"
          style={{
            background: isDarkMode
              ? "rgba(13,20,35,.98)"
              : "rgba(255,250,242,.98)",
            borderColor: isDarkMode
              ? "rgba(142,156,211,.20)"
              : "rgba(101,77,49,.13)",
          }}
        >
          <div className="px-2.5 pb-2 pt-1.5">
            <div
              className="text-[9px] font-black uppercase tracking-[.16em]"
              style={{ color: isDarkMode ? "#8F9BB0" : "#8A7A68" }}
            >
              Map layers
            </div>
            <div
              className="mt-0.5 text-[10px] font-semibold"
              style={{ color: isDarkMode ? "#AEB7C9" : "#746A60" }}
            >
              Choose what Big Blue shows.
            </div>
          </div>

          {LAYERS.map(([key, label, glyph]) => {
            const enabled = visibility[key];
            return (
              <button
                key={key}
                type="button"
                onClick={() => onToggle(key)}
                className="flex w-full items-center gap-3 rounded-[13px] px-2.5 py-2.5 text-left transition active:scale-[.99]"
                style={{
                  background: enabled
                    ? isDarkMode
                      ? "rgba(138,112,242,.08)"
                      : "rgba(47,128,237,.055)"
                    : "transparent",
                }}
                aria-pressed={enabled}
              >
                <span
                  className="grid h-7 w-7 shrink-0 place-items-center rounded-[9px] border text-[14px]"
                  style={{
                    background: isDarkMode ? "#111A2B" : "#FFFDF8",
                    borderColor: isDarkMode
                      ? "rgba(132,151,190,.14)"
                      : "rgba(91,67,49,.10)",
                    opacity: enabled ? 1 : 0.55,
                  }}
                  aria-hidden="true"
                >
                  {glyph}
                </span>
                <span
                  className="min-w-0 flex-1 text-[11px] font-black"
                  style={{
                    color: isDarkMode ? "#EEF2FA" : "#3D342D",
                    opacity: enabled ? 1 : 0.58,
                  }}
                >
                  {label}
                </span>
                <span
                  className="relative h-5 w-9 shrink-0 rounded-full transition"
                  style={{
                    background: enabled
                      ? isDarkMode
                        ? "#7057E8"
                        : "#2F80ED"
                      : isDarkMode
                        ? "rgba(255,255,255,.10)"
                        : "rgba(61,52,45,.12)",
                  }}
                  aria-hidden="true"
                >
                  <span
                    className="absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-all"
                    style={{ left: enabled ? 18 : 2 }}
                  />
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
